import React, { useState, useEffect, useRef, useCallback } from "react";
import "../css/SystemComponent.css";
import ColumnWidthToggleButton from "./Buttons/ColumnWidthToggleButton";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Yellow Square SVGs
const YellowInactiveSVG = () => (
  <svg
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Установить критическое значение (Желтый)"
  >
    <rect
      x="1.5"
      y="1"
      width="14"
      height="14"
      rx="3"
      stroke="#F3D949"
      stroke-width="2"
    />
  </svg>
);

const YellowActiveSVG = () => (
  <svg
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Установить критическое значение (Желтый)"
  >
    <rect x="0.5" width="16" height="16" rx="4" fill="#F3D949" />
    <g clipPath="url(#clip0_16107_121560)">
      <path
        d="M12.5 5.5L7 11L4.5 8.5"
        stroke="#2B3674"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_16107_121560">
        <rect
          width="12"
          height="12"
          fill="white"
          transform="translate(2.5 2)"
        />
      </clipPath>
    </defs>
  </svg>
);

// Blue Square SVGs
const BlueInactiveSVG = () => (
  <svg
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Установить критическое значение (Синий)"
  >
    <rect
      x="1.5"
      y="1"
      width="14"
      height="14"
      rx="3"
      stroke="#4F7AE2"
      stroke-width="2"
    />
  </svg>
);

const BlueActiveSVG = () => (
  <svg
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Установить критическое значение (Синий)"
  >
    <rect x="0.5" width="16" height="16" rx="4" fill="#4F7AE2" />
    <g clipPath="url(#clip0_16107_120958)">
      <path
        d="M12.5 5.5L7 11L4.5 8.5"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_16107_120958">
        <rect
          width="12"
          height="12"
          fill="white"
          transform="translate(2.5 2)"
        />
      </clipPath>
    </defs>
  </svg>
);

const SystemComponent = ({
  header,
  onConditionChange,
  onSelectionChange, // This now handles both yellow and blue selections
  selectedOption, // 'yellow', 'blue', or null
  isAllowedToChange,
  onColumnResizeToggle,
  columnData,
  headersRotated,
  isActive,
  initialRange, // Accept initial range from parent
}) => {
  // Track component mounting state
  const isInitialMountRef = useRef(true);

  // Initialize range with initialRange if provided, otherwise use defaults
  const [range, setRange] = useState(
    initialRange || {
      lower: { value: 35, condition: "<" },
      upper: { value: 80, condition: ">" },
    }
  );

  // Use ref to track previous range values to avoid unnecessary updates
  const prevRangeRef = useRef(null);

  // Ref to track if component is in the middle of an update
  const isUpdatingRef = useRef(false);

  // Error toast reference
  const errorToastActive = useRef(false);

  // Memoized function to check if ranges are equal
  const areRangesEqual = useCallback((range1, range2) => {
    if (!range1 || !range2) return false;

    return (
      range1.lower.value === range2.lower.value &&
      range1.upper.value === range2.upper.value &&
      range1.lower.condition === range2.lower.condition &&
      range1.upper.condition === range2.upper.condition
    );
  }, []);

  // Safely update parent with range changes
  const updateParent = useCallback(
    (newRange) => {
      if (isUpdatingRef.current || !onConditionChange) return;

      // Only update if the values actually changed
      if (!areRangesEqual(prevRangeRef.current, newRange)) {
        isUpdatingRef.current = true;

        // Clone to ensure we're not passing a reference that could be modified
        const rangeToSend = JSON.parse(JSON.stringify(newRange));

        // Update the parent
        onConditionChange(rangeToSend);

        // Store current range for future comparison
        prevRangeRef.current = { ...newRange };

        // Reset updating flag after a small delay to prevent potential race conditions
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 0);
      }
    },
    [onConditionChange, areRangesEqual]
  );

  // Validate range values without creating an infinite loop
  useEffect(() => {
    // Only validate if both values exist and are numbers
    if (
      range.lower.value !== "" &&
      range.upper.value !== "" &&
      !isNaN(range.lower.value) &&
      !isNaN(range.upper.value) &&
      Number(range.lower.value) > Number(range.upper.value)
    ) {
      if (!errorToastActive.current) {
        toast.error("Left value cannot be greater than right value", {
          toastId: "range-error",
          autoClose: 5000,
          closeOnClick: true,
          pauseOnHover: true,
        });
        errorToastActive.current = true;
      }
    } else {
      if (errorToastActive.current) {
        toast.dismiss("range-error");
        errorToastActive.current = false;
      }
    }
  }, [range.lower.value, range.upper.value]);

  // Run only once after the component mounts to set initial values
  useEffect(() => {
    if (isInitialMountRef.current && onConditionChange) {
      // Set the initial values just once
      prevRangeRef.current = { ...range };

      // Use a short timeout to ensure this happens after the component is fully mounted
      setTimeout(() => {
        onConditionChange(range);
        isInitialMountRef.current = false;
      }, 0);
    }
  }, [onConditionChange, range]);

  // Separate effect for updating parent when range changes (but not on initial mount)
  useEffect(() => {
    // Skip if this is the initial mount
    if (isInitialMountRef.current) return;

    // Only update if not actively updating and values actually changed
    if (
      !isUpdatingRef.current &&
      prevRangeRef.current &&
      !areRangesEqual(prevRangeRef.current, range)
    ) {
      updateParent(range);
    }
  }, [range, updateParent, areRangesEqual]);

  // Function to scan the entire column for min and max values
  const updateRangeFromColumn = useCallback(() => {
    if (!columnData || columnData.length === 0 || !isAllowedToChange) return;

    const numericValues = columnData
      .map((val) => Number(val))
      .filter((val) => !isNaN(val));

    if (numericValues.length === 0) return;

    const minVal = Math.min(...numericValues);
    const maxVal = Math.max(...numericValues);

    const newRange = {
      lower: { value: minVal, condition: "<" },
      upper: { value: maxVal, condition: ">" },
    };

    setRange(newRange);
    // No need to call updateParent here - the useEffect will handle it
  }, [columnData, isAllowedToChange]);

  // Render the placeholder dash SVG
  const DashSVG = () => (
    <svg
      width="4"
      height="3"
      viewBox="0 0 4 3"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="mx-auto"
      aria-label="Dash placeholder"
    >
      <path
        d="M0.0204688 0.728H3.86047V2.072H0.0204688V0.728Z"
        fill="#484C53"
      />
    </svg>
  );

  // Handler for when input value changes - with debouncing
  const handleValueChange = useCallback((e, rangeKey) => {
    const value = e.target.value;

    // Allow empty string or valid numbers
    if (value === "" || !isNaN(Number(value))) {
      setRange((prev) => ({
        ...prev,
        [rangeKey]: { ...prev[rangeKey], value },
      }));

      // Parent update is handled by useEffect
    }
  }, []);

  // Handler for the square selection (mutually exclusive)
  const handleSquareSelection = useCallback(
    (option) => {
      if (!onSelectionChange || !isAllowedToChange) return;

      // If user clicks on the currently selected option, deselect it
      if (selectedOption === option) {
        onSelectionChange(null);
      } else {
        // Otherwise, select the new option (switching from the other if needed)
        onSelectionChange(option);
      }
    },
    [onSelectionChange, isAllowedToChange, selectedOption]
  );

  // Reset values to empty strings to show dashes
  const handleReset = useCallback(() => {
    if (!isAllowedToChange) return;

    const newRange = {
      lower: { value: "", condition: "<" },
      upper: { value: "", condition: ">" },
    };

    setRange(newRange);

    // Also reset the selection if applicable
    if (onSelectionChange) {
      onSelectionChange(null);
    }

    // Parent update is handled by useEffect
  }, [isAllowedToChange, onSelectionChange]);

  // Common input field style with exact 40x20px dimensions
  const inputFieldClasses =
    "w-[40px] h-[20px] text-center bg-[#CBD4E7] border rounded focus:outline-none focus:ring no-arrows text-xs";

  return (
    <div className="p-4rounded-lg shadow-md bg-[#F4F7FE]">
      {/* Toggle Button: Rendered only if headersRotated is true */}
      {headersRotated && (
        <div className="flex justify-center mb-2">
          <ColumnWidthToggleButton onToggle={onColumnResizeToggle} />
        </div>
      )}

      {/* Header Section */}
      <h3 className="text-center text-sm font-semibold text-gray-600 mb-4">
        {header}
      </h3>

      {/* If the column is active (width is 30px) hide the rest of the system component */}
      {!isActive && (
        <>
          {/* Range Values Display */}
          <div className="grid grid-cols-5 items-center">
            {/* Lower Value Field */}
            <div className="flex items-center justify-center bg-gray-200 rounded-lg text-gray-700 text-sm">
              {isAllowedToChange ? (
                <div className="relative">
                  <input
                    type="text"
                    value={range.lower.value}
                    onChange={(e) => handleValueChange(e, "lower")}
                    className={inputFieldClasses}
                    style={{ width: "40px", height: "20px" }}
                  />
                  {range.lower.value === "" && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <DashSVG />
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className={
                    inputFieldClasses +
                    " inline-flex items-center justify-center"
                  }
                  style={{ width: "40px", height: "20px" }}
                >
                  {range.lower.value === "" ? <DashSVG /> : range.lower.value}
                </div>
              )}
            </div>

            <span className="flex items-center justify-center text-gray-700 font-medium text-xs">
              %
            </span>

            {/* Yellow Square */}
            <div className="flex items-center justify-center">
              <div className="w-4 h-4 bg-[#F1E323] rounded"></div>
            </div>

            <span className="flex items-center justify-center text-gray-700 font-medium text-xs">
              %
            </span>

            {/* Upper Value Field */}
            <div className="flex items-center justify-center bg-gray-200 rounded-lg text-gray-700 text-sm">
              {isAllowedToChange ? (
                <div className="relative">
                  <input
                    type="text"
                    value={range.upper.value}
                    onChange={(e) => handleValueChange(e, "upper")}
                    className={inputFieldClasses}
                    style={{ width: "40px", height: "20px" }}
                  />
                  {range.upper.value === "" && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <DashSVG />
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className={
                    inputFieldClasses +
                    " inline-flex items-center justify-center"
                  }
                  style={{ width: "40px", height: "20px" }}
                >
                  {range.upper.value === "" ? <DashSVG /> : range.upper.value}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            {/* Button 1: Scan Column for Min/Max */}
            <button
              className={`flex items-center justify-center p-3 bg-gray-200 rounded-lg ${
                isAllowedToChange
                  ? "hover:bg-gray-300 cursor-pointer"
                  : "opacity-50 cursor-not-allowed"
              }`}
              onClick={updateRangeFromColumn}
              disabled={!isAllowedToChange}
              title="Автоматическая настройка на основе минимальных и максимальных значений"
            >
              <svg
                width="24"
                height="19"
                viewBox="0 0 14 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-label="Автоматическая настройка на основе минимальных и максимальных значений"
              >
                <path
                  d="M6.66667 2.28571C6.48687 2.28571 6.3111 2.33598 6.1616 2.43017C6.0121 2.52435 5.89558 2.65822 5.82678 2.81484C5.75797 2.97147 5.73997 3.14381 5.77504 3.31008C5.81012 3.47635 5.8967 3.62908 6.02384 3.74895C6.15098 3.86882 6.31297 3.95046 6.48931 3.98353C6.66566 4.0166 6.84845 3.99963 7.01456 3.93475C7.18068 3.86988 7.32266 3.76002 7.42255 3.61906C7.52244 3.4781 7.57576 3.31238 7.57576 3.14286C7.57576 2.91553 7.47998 2.69751 7.30949 2.53677C7.139 2.37602 6.90777 2.28571 6.66667 2.28571ZM12.7273 4H12.4C12.999 3.37243 13.3313 2.55855 13.3333 1.71429C13.3333 1.56273 13.2695 1.41739 13.1558 1.31022C13.0422 1.20306 12.888 1.14286 12.7273 1.14286H10.1939C10.0689 0.8094 9.83753 0.520571 9.53155 0.315965C9.22558 0.111359 8.85997 0.000994007 8.48485 0H4.84848C4.47337 0.000994007 4.10776 0.111359 3.80178 0.315965C3.4958 0.520571 3.26443 0.8094 3.13939 1.14286H0.606061C0.445323 1.14286 0.291169 1.20306 0.177511 1.31022C0.0638526 1.41739 0 1.56273 0 1.71429C0.00202131 2.55855 0.334355 3.37243 0.933333 4H0.606061C0.445323 4 0.291169 4.0602 0.177511 4.16737C0.0638526 4.27453 0 4.41988 0 4.57143C0.00202131 5.41569 0.334355 6.22957 0.933333 6.85714H0.606061C0.445323 6.85714 0.291169 6.91735 0.177511 7.02451C0.0638526 7.13167 0 7.27702 0 7.42857C0.00159127 8.25365 0.318699 9.05052 0.893135 9.67296C1.46757 10.2954 2.2608 10.7016 3.12727 10.8171C3.24581 11.1599 3.47594 11.4587 3.78495 11.6711C4.09396 11.8834 4.46611 11.9985 4.84848 12H8.48485C8.86722 11.9985 9.23938 11.8834 9.54839 11.6711C9.85739 11.4587 10.0875 11.1599 10.2061 10.8171C11.0725 10.7016 11.8658 10.2954 12.4402 9.67296C13.0146 9.05052 13.3317 8.25365 13.3333 7.42857C13.3333 7.27702 13.2695 7.13167 13.1558 7.02451C13.0422 6.91735 12.888 6.85714 12.7273 6.85714H12.4C12.999 6.22957 13.3313 5.41569 13.3333 4.57143C13.3333 4.41988 13.2695 4.27453 13.1558 4.16737C13.0422 4.0602 12.888 4 12.7273 4ZM3.0303 9.64C2.61157 9.5378 2.22945 9.33184 1.9236 9.04346C1.61775 8.75509 1.3993 8.39481 1.29091 8H3.0303V9.64ZM3.0303 6.78286C2.61157 6.68066 2.22945 6.47469 1.9236 6.18632C1.61775 5.89794 1.3993 5.53767 1.29091 5.14286H3.0303V6.78286ZM3.0303 3.92571C2.61445 3.8288 2.23233 3.63192 1.92121 3.35429C1.62675 3.06095 1.41794 2.70066 1.31515 2.30857H3.0303V3.92571ZM9.09091 10.2857C9.09091 10.4373 9.02706 10.5826 8.9134 10.6898C8.79974 10.7969 8.64559 10.8571 8.48485 10.8571H4.84848C4.68775 10.8571 4.53359 10.7969 4.41994 10.6898C4.30628 10.5826 4.24242 10.4373 4.24242 10.2857V1.71429C4.24242 1.56273 4.30628 1.41739 4.41994 1.31022C4.53359 1.20306 4.68775 1.14286 4.84848 1.14286H8.48485C8.64559 1.14286 8.79974 1.20306 8.9134 1.31022C9.02706 1.41739 9.09091 1.56273 9.09091 1.71429V10.2857ZM11.4121 9.04571C11.101 9.32335 10.7189 9.52022 10.303 9.61714V8H12.0424C11.9327 8.39445 11.7154 8.75496 11.4121 9.04571ZM11.4121 6.18857C11.101 6.46621 10.7189 6.66308 10.303 6.76V5.14286H12.0424C11.9327 5.53731 11.7154 5.89782 11.4121 6.18857ZM11.4121 3.33143C11.101 3.60906 10.7189 3.80594 10.303 3.90286V2.28571H12.0424C11.9327 2.68016 11.7154 3.04067 11.4121 3.33143ZM6.66667 8C6.48687 8 6.3111 8.05027 6.1616 8.14445C6.0121 8.23864 5.89558 8.37251 5.82678 8.52913C5.75797 8.68575 5.73997 8.85809 5.77504 9.02436C5.81012 9.19063 5.8967 9.34336 6.02384 9.46323C6.15098 9.58311 6.31297 9.66474 6.48931 9.69782C6.66566 9.73089 6.84845 9.71391 7.01456 9.64904C7.18068 9.58416 7.32266 9.4743 7.42255 9.33335C7.52244 9.19239 7.57576 9.02667 7.57576 8.85714C7.57576 8.62982 7.47998 8.4118 7.30949 8.25105C7.139 8.09031 6.90777 8 6.66667 8ZM6.66667 5.14286C6.48687 5.14286 6.3111 5.19313 6.1616 5.28731C6.0121 5.3815 5.89558 5.51536 5.82678 5.67199C5.75797 5.82861 5.73997 6.00095 5.77504 6.16722C5.81012 6.33349 5.8967 6.48622 6.02384 6.60609C6.15098 6.72597 6.31297 6.8076 6.48931 6.84067C6.66566 6.87375 6.84845 6.85677 7.01456 6.7919C7.18068 6.72702 7.32266 6.61716 7.42255 6.4762C7.52244 6.33525 7.57576 6.16953 7.57576 6C7.57576 5.77267 7.47998 5.55465 7.30949 5.39391C7.139 5.23316 6.90777 5.14286 6.66667 5.14286Z"
                  fill="#101216"
                />
              </svg>
            </button>

            {/* Button Container for Both Squares */}
            <div className="flex items-center justify-center space-x-2">
              {/* Yellow Square Button */}
              <button
                disabled={!isAllowedToChange}
                className={`flex items-center justify-center p-1 rounded ${
                  !isAllowedToChange
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                } ${selectedOption === "yellow" ? "bg-gray-200" : ""}`}
                onClick={() => handleSquareSelection("yellow")}
                title="Установить критическое значение (Желтый)"
              >
                {selectedOption === "yellow" ? (
                  <YellowActiveSVG />
                ) : (
                  <YellowInactiveSVG />
                )}
              </button>

              {/* Blue Square Button */}
              <button
                disabled={!isAllowedToChange}
                className={`flex items-center justify-center p-1 rounded ${
                  !isAllowedToChange
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                } ${selectedOption === "blue" ? "bg-gray-200" : ""}`}
                onClick={() => handleSquareSelection("blue")}
                title="Установить критическое значение (Синий)"
              >
                {selectedOption === "blue" ? (
                  <BlueActiveSVG />
                ) : (
                  <BlueInactiveSVG />
                )}
              </button>
            </div>

            {/* Button 3: Reset to Defaults - Modified to clear values */}
            <button
              disabled={!isAllowedToChange}
              className={`flex items-center justify-center p-3 bg-gray-200 rounded-lg ${
                isAllowedToChange
                  ? "hover:bg-gray-300 cursor-pointer"
                  : "opacity-50 cursor-not-allowed"
              }`}
              onClick={handleReset}
              title="Сбросить настройки светофора до значений по умолчанию"
            >
              <svg
                width="23"
                height="22"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-label="Сбросить настройки светофора до значений по умолчанию"
              >
                <path
                  d="M8.33594 10.666C9.44994 10.666 10.3359 9.77868 10.3359 8.66602C10.3359 7.55335 9.44994 6.66602 8.33594 6.66602C7.22194 6.66602 6.33594 7.55335 6.33594 8.66602C6.33594 9.77868 7.22194 10.666 8.33594 10.666Z"
                  fill="black"
                />
                <path
                  d="M14.2139 7.45798C14.0568 6.69022 13.7499 5.96097 13.3106 5.31198C12.879 4.6735 12.3291 4.1236 11.6906 3.69198C11.0415 3.25287 10.3123 2.94591 9.5446 2.78865C9.14134 2.70687 8.73074 2.66666 8.31927 2.66865V1.33398L5.66927 3.33398L8.31927 5.33398V4.00198C8.64194 4.00065 8.9646 4.03132 9.27594 4.09532C9.87262 4.21759 10.4394 4.45612 10.9439 4.79732C11.4415 5.13281 11.8698 5.56107 12.2053 6.05865C12.7262 6.8288 13.0039 7.73756 13.0026 8.66732C13.0025 9.29124 12.8778 9.90886 12.6359 10.484C12.5183 10.761 12.3745 11.0262 12.2066 11.276C12.038 11.5242 11.8465 11.7562 11.6346 11.9687C10.9893 12.6128 10.17 13.0546 9.27727 13.24C8.65642 13.3653 8.01679 13.3653 7.39594 13.24C6.79898 13.1176 6.23196 12.8788 5.72727 12.5373C5.23026 12.2021 4.80247 11.7743 4.46727 11.2773C3.94697 10.5064 3.66907 9.59743 3.66927 8.66732H2.33594C2.33665 9.86312 2.69386 11.0315 3.36194 12.0233C3.79384 12.6607 4.34318 13.2101 4.9806 13.642C5.97102 14.3123 7.14001 14.6695 8.33594 14.6673C8.74212 14.6673 9.14727 14.6264 9.54527 14.5453C10.3124 14.3869 11.0412 14.08 11.6906 13.642C12.0095 13.4271 12.3066 13.1818 12.5779 12.9093C12.8496 12.6369 13.0951 12.3396 13.3113 12.0213C13.981 11.0312 14.338 9.86271 14.3359 8.66732C14.3359 8.26114 14.295 7.85599 14.2139 7.45798Z"
                  fill="black"
                />
              </svg>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SystemComponent;
